import { LOCAL_STORAGE_KEYS } from './constants';
import type { tInspectionFormInput } from '../types';

export type InspectionDraft = {
    equipmentCategoryId: string;
    unitId: string;
    mediaSessionId?: string;
    activeStepIndex: number;
    shouldReviewForm: boolean;
    prompts: tInspectionFormInput[];
    updatedAt: string;
};

export function saveInspectionDraft(draft: InspectionDraft): void {
    try {
        sessionStorage.setItem(LOCAL_STORAGE_KEYS.INSPECTION_DRAFT, JSON.stringify(draft));
    } catch (error) {
        console.error('Failed to save inspection draft', error);
    }
}

export function loadInspectionDraft(
    equipmentCategoryId: string,
    unitId: string,
): InspectionDraft | null {
    try {
        const raw = sessionStorage.getItem(LOCAL_STORAGE_KEYS.INSPECTION_DRAFT);
        if (!raw) {
            return null;
        }

        const draft = JSON.parse(raw) as InspectionDraft;
        if (
            draft.equipmentCategoryId !== equipmentCategoryId ||
            draft.unitId !== unitId ||
            !Array.isArray(draft.prompts) ||
            draft.prompts.length === 0
        ) {
            return null;
        }

        return draft;
    } catch {
        return null;
    }
}

export function clearInspectionDraft(): void {
    sessionStorage.removeItem(LOCAL_STORAGE_KEYS.INSPECTION_DRAFT);
}

/** Clears persisted in-progress inspection session metadata (draft + start time). */
export function clearInspectionSession(): void {
    clearInspectionDraft();
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED);
}

/**
 * Emergency reset when the UI is stuck with a stale in-progress inspection.
 * Run from the browser console on an inspect page: `resetInspectionSessionFromBrowser()`
 */
export function resetInspectionSessionFromBrowser(): void {
    clearInspectionSession();
    const params = new URLSearchParams(window.location.search);
    params.delete('action');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    window.location.reload();
}

/** Expose for support / debugging in dev and sandbox. */
if (typeof window !== 'undefined') {
    (window as Window & { resetInspectionSessionFromBrowser?: () => void }).resetInspectionSessionFromBrowser =
        resetInspectionSessionFromBrowser;
}
